import { handleServiceCall } from '../../app/util/fetch-utils';
import { envConfigs } from '../../../envConfigs';
import { foundationProductId } from '../../../helpers/productConfigHelper';
import { parse } from 'query-string';

/*
  the mobile sso login depends on sessionStorage data named
  "mobileSSOLoginData" to store information.

  The information is of three types:  that which is collected on initial
  login, that which is collected from tully's eventual response, and that
  which is added after a successful call to LPS.recommendations.

  mobileSSOLoginData:
  {
    time: timestamp when we save this so we can tell if it is stale

    ** ADDED ON LOGIN ATTEMPT
    ui_language: supplied by the mobile app on login (OPTIONAL)
    sso_code: this is the org's namespace, which the mobile apps might have
              cached from a previous successful login (OPTIONAL)
    app_id: supplied by the mobile app on login so we know if this is the
              correct app for the user to be logging in to (REQUIRED)

    ** ADDED AFTER TULLY LOGIN
    token:  returned by tully on successful login
    userId:  returned by tully on successful login
    email:  returned by tully on successful login

    ** ADDED AFTER RECOMMENDATIONS
    days_until_trigger:  filled in from recommendation on successful login
    product_name: filled in from recommendation on successful login to wrong app
    happyUrl: this is the URI we redirect to at the end of a successful
              mobile sso login.  It tells the mobile apps that success was attained
              and it contains information which they use to do their next thing
  }
 */
const mobileSSOLoginDataName = 'mobileSSOLoginData';

// Mobile apps that support products with different product Ids need to be listed here.
// No need to list the app with its own product Id, that support is implied.
const appIdSupportedProductsMap = {
  'product.6a1a08c9-7e8d-c5cc-58a8-96e93a35df94': [
    // FluencyBuilder app runs Webi products below
    'product.8c3d3ec6-aa91-e298-0838-c87f9c63492d', // Webi English
    'product.a7d387bb-79c6-771a-9ea3-9e4025133680' // Webi SpeakHI
  ]
};

// PLAT-539: some customers launch the SSO login from their IdP portal as a fresh SSO login
// cycle, which trashes the existing mobile SSO login logic and converts the mobile SSO login
// into a web SSO login by removing the MobileLoginSessionData. This methods is meant to
// preserve the MobileLoginSessionData in these cases.
export const shouldPreserveMobileLoginSessionData = (location) => {
  const queryParams = parse(location.search);
  if (queryParams.clientId) {
    let mobileData = window.sessionStorage.getItem(mobileSSOLoginDataName);
    if (mobileData) {
      mobileData = JSON.parse(mobileData);
      if (mobileData.sso_code && mobileData.org_guid && mobileData.time) {
        // if MobileLoginSessionData saved recently and sso_code orgId matches clientId query param in SSO login URL, save the data
        const nowMs = Date.now();
        const sessionTimeoutMs = 3600000; // give them an hour to navigate the IdP portal and choose RS
        if (mobileData.time > nowMs - sessionTimeoutMs && mobileData.org_guid === queryParams.clientId) {
          // cached MobileLoginSessionData was for this org, so keep it
          return true;
        }
      }
    }
  }
  // no clientId query param, stale session, orgId mismatch, so any existing MobileLoginSessionData is pointless
  return false;
};

export const clearMobileLoginSessionData = () => {
  window.sessionStorage.removeItem(mobileSSOLoginDataName);
};

export const getMobileLoginSessionData = () => {
  let mobileData = window.sessionStorage.getItem(mobileSSOLoginDataName);
  if (mobileData) {
    mobileData = JSON.parse(mobileData);
  }
  return mobileData;
};

export const getAndClearMobileLoginSessionData = () => {
  let mobileData = getMobileLoginSessionData();
  if (mobileData) {
    window.sessionStorage.removeItem(mobileSSOLoginDataName);
  }
  return mobileData;
};

export const saveMobileLoginSessionData = (mobileLoginData) => {
  if (typeof mobileLoginData === 'undefined') {
    window.sessionStorage.removeItem(mobileSSOLoginDataName);
  } else {
    window.sessionStorage.setItem(mobileSSOLoginDataName, JSON.stringify(mobileLoginData));
  }
};

export const serializeHappyUrl = (mobileSSOLoginData) => {
  let successUri = '/#/mobilessologinsuccess';

  return (
    successUri +
    '?sso_code=' +
    mobileSSOLoginData.sso_code +
    '&rst_access_token=' +
    mobileSSOLoginData.token +
    '&user_id=' +
    mobileSSOLoginData.userId +
    '&email=' +
    mobileSSOLoginData.email
  );
};

export const serializeHappyDeepLink = (mobileSSOLoginData) => {
  const schema = mobileSSOLoginData.app_id === foundationProductId ? 'rstotalecompanion' : 'rosettastone';
  let successUri = schema + '://mobilessologinsuccess';

  return (
    successUri +
    '?sso_code=' +
    mobileSSOLoginData.sso_code +
    '&rst_access_token=' +
    mobileSSOLoginData.token +
    '&user_id=' +
    mobileSSOLoginData.userId +
    '&email=' +
    mobileSSOLoginData.email
  );
};

/*
RSCP-2345 / RSCP-2522:
The changes for api_version 2 were added to support Mobile SSO DeviceID authentication. Essentially, we need to
support "Default Phone Browser" launch for SSO handshake instead of using the default WebView. Words for
researching this are ASWebAuthenticationSession on iOS and Custom Tabs on Android. Use-case example:

Company auth setup process puts secrets on user's phone where MS Edge can get them. When phone wants to auth,
when SSO handshake goes to azure IdP, we need to be using Edge instead of Webview, because Edge can see those
secrets, e.g. verify DeviceId.

The major hangup is: the permissions for WebView were that the mobile app could still "snoop" on urls and
decide when to jump back into the app. With api_version 2, the app hands off user-focus to the default phone
browser, and launchpad now has to explicitly use a schema to tell the app to launch again.

We now store both the in-app relative URL in happyUrl, and a deep link with the custom schema in happyDeepLink.
We need to preserve both the in-app URL for navigation within the app (props.history.push(happyUrl)) as well as
navigation back into the mobile app (window.location.assign(happyDeepLink)).
*/
export const installHappyUrl = (mobileSSOLoginData) => {
  mobileSSOLoginData.happyUrl = serializeHappyUrl(mobileSSOLoginData);
  const apiVersion = mobileSSOLoginData.api_version ? mobileSSOLoginData.api_version.toString() : '1';
  if (apiVersion === '2') {
    mobileSSOLoginData.happyDeepLink = serializeHappyDeepLink(mobileSSOLoginData);
  }
};

export const getSamlEntityIdFromOrgId = (orgId) => {
  const egoUrl = envConfigs.egoApiHost + '/api/authentication/samlsso/' + orgId;
  return fetch(egoUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Cache-Control': 'no-store, no-cache, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache'
    }
  }).then((response) => {
    return handleServiceCall(response).then((orgInfo) => {
      if (!orgInfo || !orgInfo.samlEntityId) {
        throw new Error('getSamlEntityIdFromOrgId error: org not found or org has no samlEntityId');
      }
      return orgInfo.samlEntityId;
    });
  });
};

export const getSkeletonOrgFromNamespace = (namespace) => {
  const egoUrl = envConfigs.egoApiHost + '/api/authentication/samlssoByNamespace/' + namespace;
  return fetch(egoUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Cache-Control': 'no-store, no-cache, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache'
    }
  }).then((response) => {
    return handleServiceCall(response).then((orgInfo) => {
      return orgInfo;
    });
  });
};

// Utility helper to see if the given app supports the given product.
// All apps support their own product, obviously, but some apps support additional products, the
// first case being WEBI products running on FluencyBuilder.
export const validateAppId = (appId, productId) => {
  return (
    appId === productId || (appIdSupportedProductsMap[appId] && appIdSupportedProductsMap[appId].includes(productId))
  );
};



// WEBPACK FOOTER //
// ./src/modules/login/services/sso-helpers.js